/* eslint-disable no-unused-vars */
/* global PLATFORM, G */
import { asyncPipeSpread, asyncPipeSpreadIf, isArr, isObj, setKey } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import { get, set } from 'lib/sequence/component/state/value'
import sequenceComponentState from 'lib/sequence/component/state'
import asObject from 'lib/sequence/component/children/asObject'
import { show, hide } from 'lib/sequence/component/state/hidden'
import { set as setToken } from 'app/_shared/component/token'
import getContextTabs from 'app/_shared/events/tabs/getContextTabs'
import remember from 'app/_shared/events/remember'
import finish from 'app/_shared/action/partial/finish'

const {
  set: setSearchMode,
} = sequenceComponentState('fromSearch')

/**
 * Checks whether `component` has been cached yet.
 *
 * @param {Gaia.AppModule.Spec} module       current module composition
 * @param {Gaia.Component.Spec} component current component
 * @returns {boolean}
 */
const cached = (module, component) => {
  const path = component[G.PROPS][G.ROUTE]
  const actionData = module[G.STATE][G.ACTION][G.DATA]

  return !!path.reduce((acc, key) => (isObj(acc) && acc[key] ? acc[key] : false), actionData)
}

/**
 * Maps the current search term coming from the index action to the search field.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapSearchField = module => async (components, ...args) => {
  const { term } = args[0] || {}

  const { searchField } = components

  // If we are coming from the index action, we have a term, but it's not cached yet. We need to
  // cache it now so that if the user clicks on an item and then goes back, the search term is
  // still there
  !cached(module, searchField)
    && term
    && await remember(module, searchField, { detail: { value: term } })

  term && set(searchField, term)

  return [components, ...args]
}

/**
 * Focuses the search field if `args` has a truthy `focusSearchField` property.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const focusSearchField = module => async (components, ...args) => {
  const action = module[G.STATE][G.ACTION]
  const ui = action[G.UI]
  const { focusSearchField: focus } = args?.[0] || {}

  ui?.search && (ui.search.focused = PLATFORM === 'mobile' && focus)

  return [components, ...args]
}

/**
 * Maps the current search term coming from the index action to the search results label.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapTerm = module => async (components, ...args) => {
  const { term } = args[0] || {}

  const tabs = getContextTabs(module, null, null)

  const { term: termField, searchField, noTerm } = components
  const currentSearchTerm = get(searchField)
  // our results select field is an Autocomplete component, thus it being empty may mean that its
  // value is an empty array
  const hasTerm = isArr(term) ? term.length : term
  const hasCurrentTerm = isArr(currentSearchTerm) ? currentSearchTerm.length : currentSearchTerm
  const hasSearchTerm = hasCurrentTerm || hasTerm

  hasSearchTerm
    ? hide(noTerm) && show(termField)
    : hide(termField) && show(noTerm)

  currentSearchTerm && setToken(termField, { term: currentSearchTerm })

  // setting fromSearch prop for all lists so that they display the proper NoData component
  tabs[G.CHILDREN].forEach((child) => {
    const { list } = asObject(child[G.CHILDREN])
    setSearchMode(list, !!hasSearchTerm)
  })

  return [components, ...args]
}

/**
 * Sets the search term for each tab and also resets the tabs labels.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setTabSearch = module => async (components, ...args) => {
  const { term } = args[0] || {}

  // component
  const tabs = getContextTabs(module, null, null)
  const tabsChildren = tabs[G.CHILDREN] // no asObject here

  const currentSearchTerm = get(components.searchField)

  try {
    // Setting the current search tab to the lists so that they render properly
    tabsChildren.forEach((tab) => {
      const { list } = find(tab)
      const listState = list[G.STATE]
      setKey({ ...listState[G.META], term: term || currentSearchTerm }, G.META, listState)
    })
  } catch (e) {
    console.error(e)
  }

  return [components, ...args]
}

/**
 * EquipmentInformation Equipment Action
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*): function(...[*]): Promise<*[]>}
 */
export default module => () => async (...args) => asyncPipeSpread(
  mapSearchField(module),
  focusSearchField(module),
  mapTerm(module),
  asyncPipeSpreadIf(() => PLATFORM !== 'mobile')(
    setTabSearch(module),
  ),
  finish,
)(find(module[G.STATE][G.ACTION][G.COMPONENT]), ...args)
